import { Dispatch, SetStateAction, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CampaignCreationCardHeader } from '@/components/glitch-ui/campaigncreationcardheader';
import { GoogleAdsAccountList } from '@/components/glitch-ui/googleads-account-list';
import type { GoogleAdsAccount } from '@/lib/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  googleAdsAccountsQueryOptions,
  useSetGoogleAdsCustomerIdMutationOptions,
} from '@/lib/query-options';
import { useAtomValue, useSetAtom } from 'jotai';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';
import { useToast } from '@/hooks/useToast';
import { ONBOARDING_CAMPAIGN_CREATION_STEP } from './enums';
import { usePostHog } from 'posthog-js/react';
import { useUserProfile } from '@/contexts/user.context';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
interface SelectGoogleAdsAccountProps {
  setCurrentStep: Dispatch<SetStateAction<ONBOARDING_CAMPAIGN_CREATION_STEP>>;
  setStepValid: (valid: boolean) => void;
  setIsWarningErrorSuccess: (isWarning: boolean) => void;
}


export const SelectGoogleAdsAccount = ({ setStepValid, setIsWarningErrorSuccess }: SelectGoogleAdsAccountProps) => {
  const { data: accounts = [] } = useSuspenseQuery(googleAdsAccountsQueryOptions());
  const googleAdsCustomerMutation = useSetGoogleAdsCustomerIdMutationOptions();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const currentOrganization = useAtomValue(organizationDataAtom);
  const { toast } = useToast();
  const posthog = usePostHog();
  const { user } = useUserProfile();
  
  // Track Google Ads account selection screen view
  useEffect(() => {
    posthog?.capture('onboarding_google_ads_account_selection_viewed', {
      accounts_available: accounts.length,
      has_connected_accounts: accounts.some(account => account.linked)
    });
  }, [accounts]);


  const handleAccountSelect = async (account: GoogleAdsAccount) => {
    try {
      // Track account selection
      posthog?.capture('onboarding_google_ads_account_selected', {
        account_id: account.googleads_account_id,
        is_manager_account: account.manager_account_id !== null,
        is_linked: account.linked,
      });

      await googleAdsCustomerMutation.mutateAsync({
        googleAdsAccountId: account.googleads_account_id,
        organizationId: currentOrganization?.id
      });
      setGoogleAdsAccount(account);
      setStepValid(true);
    } catch (error) {
      console.error('❌ Google Ads account switch (onboarding) failed:', error);
      toast({ description: 'Error selecting account', variant: 'destructive' });
    }
  };

  // Determine account state
  const hasGoogleAccount = !!user?.email;
  const hasGoogleAdsAccounts = accounts.length > 0;
  
  // Update validation based on current selection
  useEffect(() => {
    setStepValid(!!googleAdsAccount);
  }, [googleAdsAccount]);

  // Set warning state based on whether we're showing warning cards
  useEffect(() => {
    const showingWarning = !hasGoogleAdsAccounts || !hasGoogleAccount;
    setIsWarningErrorSuccess(showingWarning);
  }, [hasGoogleAdsAccounts, hasGoogleAccount, setIsWarningErrorSuccess]);
  
  // Case 1: User has Google Ads accounts (working)
  if (hasGoogleAccount && hasGoogleAdsAccounts) {
    return (
      <motion.div 
        className='campaign-goal-select flex-row space-y-6 !mt-0'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <div>
          <Card className='border-none shadow-none'>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <CampaignCreationCardHeader
                title=''
                subTitle=''
              />
            </motion.div>
            <CardContent className='m-3 p-2 space-y-6 rounded-xl'>
              <motion.div
                className='rounded-xl pt-4 px-8 pb-8'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <GoogleAdsAccountList
                  accounts={accounts}
                  selectedAccountId={googleAdsAccount?.googleads_account_id}
                  onSelect={handleAccountSelect}
                  animated={true}
                  listClassName="max-h-[36vh] overflow-y-auto space-y-3"
                />
              </motion.div>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    );
  }

  // Case 2: User has Google account but no Google Ads accounts
  if (hasGoogleAccount && !hasGoogleAdsAccounts) {
    return (
      <motion.div 
        className='campaign-goal-select flex-row space-y-6 !mt-0'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <motion.div 
          className='bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-6'
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className='space-y-6'>
            <div className='flex items-start space-x-4'>
              <motion.div 
                className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <AlertCircle className="h-8 w-8" style={{ color: '#f59e0b' }} />
              </motion.div>
              <motion.div 
                className='flex-1 space-y-4'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">No Google Ads account found</h2>
                  <p className="text-base text-slate-900 leading-relaxed">
                    We couldn't find any Google Ads accounts linked to your account ({user?.email}). 
                    Glitch must be connected to a Google Ads account to create and manage campaigns.
                  </p>
                </div>
                <motion.div 
                  className='flex space-x-3'
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                >
                  <a
                    href='https://ads.google.com/start/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Create Google Ads account
                    <ExternalLink className='h-4 w-4' />
                  </a>
                  {/* <Button
                    variant="outline"
                    size="lg"
                    className="w-[260px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
                  >
                    <span>CONNECT AN EXISTING ACCOUNT</span>
                  </Button> */}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Case 3: User not signed up with Google
  return (
    <motion.div 
      className='campaign-goal-select flex-row space-y-6 !mt-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <div>
        <Card className='border-none shadow-none'>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <CampaignCreationCardHeader
              title='Sign in with Google'
              subTitle='Connect your Google account to access Google Ads'
            />
          </motion.div>
          <CardContent className='m-6 space-y-6'>
            <div className='text-center space-y-4'>
              <motion.div 
                className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <AlertCircle className="h-8 w-8" style={{ color: '#6A6A8E' }} />
              </motion.div>
              <motion.h3 
                className="text-3xl font-semibold text-center bg-gradient-to-r from-[#6A6A8E] to-[#4A4A6E] bg-clip-text text-transparent"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                Authentication Required
              </motion.h3>
              <motion.p 
                className="text-base text-slate-900 leading-relaxed"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                You need to be signed in with a Google account to access Google Ads functionality.
              </motion.p>
              <motion.div 
                className="bg-white border border-gray-200 rounded-lg p-4 text-left space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                <p className="text-sm text-slate-500 font-medium">This allows us to:</p>
                <div className="text-base text-slate-900 space-y-1">
                  <p>• Access your Google Ads accounts</p>
                  <p>• Create and manage campaigns</p>
                  <p>• Track performance metrics</p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.6 }}
              >
                <Button
                  onClick={() => window.location.href = '/signin'}
                  size="lg"
                  className="px-8 py-3 bg-[#290BD9] hover:bg-[#2309C5]"
                >
                  Sign in with Google
                </Button>
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};
